import { RoutineType, WorkspaceAsset, WorkspaceRoutine, DayIntervalUnit, MS_TO_UNITS, formatDateToMMDDYYYY } from '@gripp/shared-logic';
import { AlertType } from './types';

export const getCounterRoutinesForOnInterval = (
  workspaceAsset: WorkspaceAsset
): WorkspaceRoutine[] => {
  const counterTypeRoutines =
    workspaceAsset.routines?.filter(
      (routine) => routine.type === RoutineType.Counter
    ) || [];

  const counterRoutinesWithState = counterTypeRoutines.map((routine) => {
    const routineId = routine.id;
    const state = workspaceAsset.routineState?.find(
      (s) => routineId === s.workspaceRoutineId
    );
    return { ...routine, state: state?.state };
  });

  return counterRoutinesWithState;
};

export const getAlertType = (key: string) => {
  switch (key) {
    case 'onDate':
      return AlertType.Date;
    case 'dayIntervalUnit':
      return AlertType.RecurringInterval;
    case 'daysSinceCompleted':
      return AlertType.DaysSkipped;
    case 'onCompletionCount':
      return AlertType.RoutineCompletions;
    case 'floor':
      return AlertType.Max;
    case 'ceiling':
      return AlertType.Min;
    case 'every':
      return AlertType.Days;
    case 'onInterval':
      return AlertType.Interval;
    default:
      return undefined;
  }
};

/**
 * Calculates either the next alert date or days elapsed for recurring incomplete alerts.
 * 
 * This function handles alerts that trigger after a certain number of days have passed
 * without a completion (e.g., "alert me if X days pass without completing this routine").
 * It accounts for repeating intervals by calculating which cycle we're in.
 * 
 * @param date - The reference date (start date, last completion, or workflow creation date)
 * @param daysVal - The number of days in each alert cycle
 * @param getDate - If true, returns the next alert date; if false, returns days elapsed
 * @returns Either a formatted date string or the number of days elapsed in the current cycle
 */
export const getSkippedCompletionDateOrDays = (
  date: Date,
  daysVal: number,
  getDate: boolean
): string | number => {
  if (!date || !daysVal || daysVal <= 0) return 'error';

  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + Number(daysVal));

  const now = new Date();
  const lastUpdated = new Date(date);
  let daysElapsed = Math.abs(
    Math.floor(
      (now.getTime() - lastUpdated.getTime()) /
        MS_TO_UNITS[DayIntervalUnit.Days]
    )
  );

  while (nextDate < now || daysElapsed >= daysVal) {
    nextDate.setDate(nextDate.getDate() + Number(daysVal));
    daysElapsed = daysElapsed - daysVal;
  }

  return getDate ? formatDateToMMDDYYYY(nextDate) : daysElapsed;
};